import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { TelegramLogoIcon, PaperPlaneTiltIcon, SpinnerGapIcon, CheckCircleIcon, GearSixIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { useAuth } from "../context/AuthContext";
import { fetchAlertConfig, fetchAlertSettings, saveAlertSettings, sendTestAlert } from "../lib/api";

export const TelegramConnect = () => {
  const clientId = getClientId();
  const { requireAuth } = useAuth();
  const configQ = useQuery({ queryKey: ["alertConfig"], queryFn: fetchAlertConfig });
  const settingsQ = useQuery({ queryKey: ["alertSettings", clientId], queryFn: () => fetchAlertSettings(clientId) });
  const [chatId, setChatId] = useState("");

  useEffect(() => {
    if (settingsQ.data?.chatId) setChatId(settingsQ.data.chatId);
  }, [settingsQ.data]);

  const connected = settingsQ.data?.chatId && settingsQ.data?.enabled;

  const connectMut = useMutation({
    mutationFn: async () => {
      await saveAlertSettings(clientId, {
        chatId: chatId.trim(),
        enabled: true,
        minScore: settingsQ.data?.minScore ?? 75,
        triggers: settingsQ.data?.triggers || {},
        watchlist: settingsQ.data?.watchlist || [],
      });
      return sendTestAlert(clientId);
    },
    onSuccess: () => { toast.success("Connected! Check Telegram for a test ping."); settingsQ.refetch(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't connect — press Start on the bot first."),
  });

  if (!configQ.data?.botConfigured) return null;

  return (
    <div data-testid="radar-telegram-connect" className="border border-neon/25 bg-neon/[0.03] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-3 shrink-0">
        <div className="w-10 h-10 flex items-center justify-center bg-neon/15 text-neon border border-neon/30">
          <TelegramLogoIcon size={22} weight="fill" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-sm">
            {connected ? "Telegram alerts connected" : "Get instant Telegram alerts"}
          </div>
          <div className="text-xs text-muted-foreground">
            {connected
              ? <>Pinging chat <span className="font-mono text-neon">{settingsQ.data.chatId}</span></>
              : <>Message <a href="https://t.me/DdotfirstBot" target="_blank" rel="noreferrer" className="text-neon hover:underline">@DdotfirstBot</a> → get your ID from <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-neon hover:underline">@userinfobot</a></>}
          </div>
        </div>
      </div>

      {connected ? (
        <div className="flex items-center gap-2 sm:ml-auto">
          <span className="flex items-center gap-1.5 text-neon text-sm font-medium"><CheckCircleIcon size={16} weight="fill" /> Active</span>
          <Link to="/alerts" data-testid="radar-manage-alerts" className="flex items-center gap-1.5 px-3 py-2 border border-white/15 text-sm hover:border-neon/40 hover:text-neon transition-colors">
            <GearSixIcon size={15} /> Manage
          </Link>
        </div>
      ) : (
        <div className="flex gap-2 sm:ml-auto w-full sm:w-auto">
          <input
            data-testid="radar-chatid-input"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="Your Telegram chat ID"
            className="flex-1 sm:w-52 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
          />
          <button
            data-testid="radar-connect-btn"
            onClick={() => { if (requireAuth("connect Telegram alerts")) connectMut.mutate(); }}
            disabled={!chatId.trim() || connectMut.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {connectMut.isPending ? <SpinnerGapIcon size={15} className="animate-spin" /> : <PaperPlaneTiltIcon size={15} weight="bold" />}
            Connect
          </button>
        </div>
      )}
    </div>
  );
};
